import type { BoardSwitcherSection } from '@trello/board-switcher';

/**
 * The BoardSwitcher component contains a number of keyboard navigable elements
 * (BoardSwitcherTile, CreateBoardTile, WorkspaceViewTile, etc.), but when we
 * manage focus state, we don't necessarily want that component to be focusable
 * (as it's a div). Instead, we want to find the first focusable element within
 * each element (most commonly an anchor tag), and focus that instead. This
 * allows us to meet HTML5 accessibility standards, while also allowing each
 * different component to manage its own internals.
 *
 * Even though using querySelectors is a bit of an anti-pattern in React,
 * we have to access this flow from a few different origins, including keyboard
 * shortcuts, which are set up on the BoardSwitcher component rather than on each
 * focusable component.
 */
export const focusBoardSwitcherNavItem = (
  section: BoardSwitcherSection | null,
  id: string | null,
  shouldScrollIntoView: boolean = true,
) => {
  if (!id) return;

  // eslint-disable-next-line @trello/no-query-selector
  const focusableElement = document.querySelector(
    `[data-nav-item][data-section="${section}"][data-id="${id}"]`,
  );

  if (!(focusableElement instanceof HTMLElement)) return;

  focusableElement.focus({ preventScroll: true });

  // Element.scrollIntoView is not implemented in jsdom.
  // See: https://github.com/jsdom/jsdom/issues/1695
  if (shouldScrollIntoView && focusableElement?.scrollIntoView) {
    focusableElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }
};
